import { Color, Spacing, Typography } from '@persona-id/design-tokens';
import { ArrowRightIcon, CircleInfoIcon } from '@persona-id/design-tokens/icons';
import styled from 'styled-components';

import { AvailableOrganizationPlan, OrganizationPlanKey } from 'dashboard/models/organization-plan';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import EnterpriseIcon from 'images/dashboard/plans/enterprise.svg';
import EssentialIcon from 'images/dashboard/plans/essential.svg';
import GrowthIcon from 'images/dashboard/plans/growth.svg';
import StartupProgramImage from 'images/dashboard/plans/startup-program.svg';

export interface PlansTableProps {
  currentOrganizationPlanKey: OrganizationPlanKey;
  availablePlans: AvailableOrganizationPlan[];
  hasPendingPlanMigration: boolean;
  openPlanSelectionModal: (requestedOrganizationPlan: AvailableOrganizationPlan) => void;
  openTalkToSalesLink: () => void;
}

const PlansTable = ({
  currentOrganizationPlanKey,
  availablePlans,
  hasPendingPlanMigration,
  openPlanSelectionModal,
  openTalkToSalesLink,
}: PlansTableProps) => {
  const availablePlanKeys = availablePlans.map((plan) => plan.key);

  function findAvailablePlanByKey(
    planKey: OrganizationPlanKey,
  ): AvailableOrganizationPlan | undefined {
    return availablePlans.find((plan) => plan.key === planKey);
  }

  const essentialButton = (
    hasPendingPlanMigration: boolean,
    currentOrganizationPlanKey: OrganizationPlanKey,
  ) => {
    if (hasPendingPlanMigration) {
      return (
        <Tooltip
          side='top'
          trigger={
            <Button
              id='plans-table__essential-select'
              data-test='plans-table__essential-select'
              aria-label='plans-table__essential-select'
              variant='primary'
              disabled={true}
            >
              Select plan
            </Button>
          }
        >
          This option is unavailable while we are processing your current plan request.
        </Tooltip>
      );
    } else if (OrganizationPlanKey.Essential === currentOrganizationPlanKey) {
      return (
        <Button
          id='plans-table__essential-select'
          data-test='plans-table__essential-select'
          aria-label='plans-table__essential-select'
          disabled={true}
          variant='basic'
        >
          You’re currently on this plan
        </Button>
      );
    } else if (availablePlanKeys.includes(OrganizationPlanKey.Essential)) {
      return (
        <Button
          id='plans-table__essential-select'
          data-test='plans-table__essential-select'
          aria-label='plans-table__essential-select'
          variant='primary'
          onClick={() =>
            openPlanSelectionModal(findAvailablePlanByKey(OrganizationPlanKey.Essential)!)
          }
        >
          Select plan
        </Button>
      );
    } else {
      return (
        <Button
          id='plans-table__essential-select'
          data-test='plans-table__essential-select'
          aria-label='plans-table__essential-select'
          variant='primary'
          onClick={() => openTalkToSalesLink()}
        >
          Talk to sales
        </Button>
      );
    }
  };

  const growthButton = (currentOrganizationPlanKey: OrganizationPlanKey) => {
    if (
      [OrganizationPlanKey.Growth, OrganizationPlanKey.TrialGrowth].includes(
        currentOrganizationPlanKey,
      )
    ) {
      return (
        <Button
          id='plans-table__growth-select'
          data-test='plans-table__growth-select'
          aria-label='plans-table__growth-select'
          disabled={true}
          variant='basic'
        >
          You’re currently on this plan
        </Button>
      );
    } else {
      return (
        <Button
          id='plans-table__growth-select'
          data-test='plans-table__growth-select'
          aria-label='plans-table__growth-select'
          variant='primary'
          onClick={() => openTalkToSalesLink()}
        >
          Talk to sales
        </Button>
      );
    }
  };

  const enterpriseButton = () => {
    return (
      <Button
        id='plans-table__enterprise-select'
        data-test='plans-table__enterprise-select'
        aria-label='plans-table__enterprise-select'
        variant='primary'
        onClick={() => openTalkToSalesLink()}
      >
        Talk to sales
      </Button>
    );
  };

  const starterButton = (hasPendingPlanMigration: boolean) => {
    if (hasPendingPlanMigration) {
      return (
        <Tooltip
          side='top'
          trigger={
            <Button
              id='plans-table__starter-select'
              data-test='plans-table__starter-select'
              aria-label='plans-table__starter-select'
              variant='primary'
              width='button'
              disabled={true}
            >
              Apply
            </Button>
          }
        >
          This option is unavailable while we are processing your current plan request.
        </Tooltip>
      );
    } else {
      return (
        <>
          {findAvailablePlanByKey(OrganizationPlanKey.Starter) && (
            <Button
              id='plans-table__starter-select'
              data-test='plans-table__starter-select'
              aria-label='plans-table__starter-select'
              variant='primary'
              width='button'
              onClick={() =>
                openPlanSelectionModal(findAvailablePlanByKey(OrganizationPlanKey.Starter)!)
              }
            >
              Apply for General Cohort
            </Button>
          )}
          {findAvailablePlanByKey(OrganizationPlanKey.StarterPenguin) && (
            <Button
              id='plans-table__starter-fintech-select'
              aria-label='plans-table__starter-fintech-select'
              variant='primary'
              width='button'
              onClick={() =>
                openPlanSelectionModal(findAvailablePlanByKey(OrganizationPlanKey.StarterPenguin)!)
              }
            >
              Apply for Fintech Cohort
            </Button>
          )}
        </>
      );
    }
  };

  return (
    <>
      <CompareAllPlansWrapper>
        <a href='https://withpersona.com/pricing' target='_blank' rel='noreferrer'>
          <Button size='md' variant='basic' suffixIcon={<ArrowRightIcon />}>
            Compare all plans
          </Button>
        </a>
      </CompareAllPlansWrapper>
      <Spacer height={2} />
      <PlansRowWrapper>
        <Card>
          <Card.Body>
            <EssentialIcon />
            <Spacer height={1} />
            <PlanTitle>Essential Plan</PlanTitle>
            <PlanSubtitleWrapper>
              <PlanSubtitle>Starting at $250/month</PlanSubtitle>
              <Tooltip iconTrigger={<CircleInfoIcon color={Color.CorePurple800} />}>
                <TooltipContentWrapper>
                  <Paragraph>
                    Designed to help you get started, the Essential plan includes 500 free services
                    per month (services in excess of 500 will be charged $1.50). Volume-discounted
                    and custom pricing are available on other plans.
                  </Paragraph>
                </TooltipContentWrapper>
              </Tooltip>
            </PlanSubtitleWrapper>
            <Spacer height={1} />
            <Paragraph size='lg'>
              Get started quickly with Persona’s most popular products & services
            </Paragraph>
          </Card.Body>
        </Card>
        <Card>
          <Card.Body>
            <GrowthIcon />
            <Spacer height={1} />
            <PlanTitle>Growth Plan</PlanTitle>
            <PlanSubtitle>Volume-based pricing starting at $1,250/month</PlanSubtitle>
            <Spacer height={1} />
            <Paragraph size='lg'>
              Scale your identity operations with Persona’s fully configurable platform
            </Paragraph>
          </Card.Body>
        </Card>
        <Card>
          <Card.Body>
            <EnterpriseIcon />
            <Spacer height={1} />
            <PlanTitle>Enterprise Plan</PlanTitle>
            <PlanSubtitle>Custom pricing</PlanSubtitle>
            <Spacer height={1} />
            <Paragraph size='lg'>Fully tailored solutions for all of your identity needs</Paragraph>
          </Card.Body>
        </Card>
      </PlansRowWrapper>
      <PlansRowWrapper>
        <Card>
          <Card.Body>
            <PlanHeading>What you get</PlanHeading>
            <UnorderedList>
              <ListItem>
                Access to templated and pre-configured industry-standard identity services
              </ListItem>
              <ListItem>
                Platform features to automate business logic and streamline team productivity
              </ListItem>
              <ListItem>
                Self-guided integration and onboarding experience with product support via email
              </ListItem>
              <ListItem>Hosting on secure, enterprise-grade SOC 2 Type 2 infrastructure</ListItem>
            </UnorderedList>
            <Spacer height={1} />
            {essentialButton(hasPendingPlanMigration, currentOrganizationPlanKey)}
          </Card.Body>
        </Card>
        <Card>
          <Card.Body>
            <PlanHeading>Everything in Essential, plus</PlanHeading>
            <UnorderedList>
              <ListItem>
                Flexibility to customize your plan with Persona’s full suite of tools as your needs
                evolve
              </ListItem>
              <ListItem>
                Access to advanced add-on products like Graph, KYB, and compliance-focused solutions
              </ListItem>
              <ListItem>
                Guided implementation and configuration experience with success team
              </ListItem>
              <ListItem>Advanced security configuration (redaction, user permissions)</ListItem>
            </UnorderedList>
            <Spacer height={1} />
            {growthButton(currentOrganizationPlanKey)}
          </Card.Body>
        </Card>
        <Card>
          <Card.Body>
            <PlanHeading>Everything in Growth, plus</PlanHeading>
            <UnorderedList>
              <ListItem>
                Partner with us to build fully bespoke identity programs for KYC/AML & more
              </ListItem>
              <ListItem>
                Custom multi-organization partitioning, access controls, and support
              </ListItem>
              <ListItem>Early access to new and cutting-edge products and services</ListItem>
              <ListItem>Get more with custom multi-product pricing structures</ListItem>
            </UnorderedList>
            <Spacer height={1} />
            {enterpriseButton()}
          </Card.Body>
        </Card>
      </PlansRowWrapper>
      {(availablePlanKeys.includes(OrganizationPlanKey.Starter) ||
        availablePlanKeys.includes(OrganizationPlanKey.StarterPenguin)) && (
        <>
          <Spacer height={2} />
          <StartupProgramCard>
            <Card.Body>
              <StartupProgramContentWrapper>
                <div>
                  <StartupProgramPlanTitle>
                    For small businesses, startups, and developers
                  </StartupProgramPlanTitle>
                  <Spacer height={2} />
                  <StartupProgramParagraph size='md'>
                    Eligible businesses, founders, and developers can apply to join
                    industry-specific cohorts of the Persona Startup Program. The program includes a
                    monthly allotment of free services (limited to Government ID & Selfie
                    Verifications and Watchlist Reports), an invitation to the Personally community,
                    and more.
                  </StartupProgramParagraph>
                  <Spacer height={4} />
                  <StartupProgramButtons>
                    {starterButton(hasPendingPlanMigration)}
                    <a href='https://withpersona.com/startups' target='_blank' rel='noreferrer'>
                      <Button size='md' variant='basic' suffixIcon={<ArrowRightIcon />}>
                        Learn more
                      </Button>
                    </a>
                  </StartupProgramButtons>
                </div>
                <StartupProgramImage />
              </StartupProgramContentWrapper>
            </Card.Body>
          </StartupProgramCard>
        </>
      )}
    </>
  );
};

const CompareAllPlansWrapper = styled('div')`
  display: flex;
  justify-content: flex-end;
`;

const PlansRowWrapper = styled('div')`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: 1fr 1fr 1fr;
  grid-column-gap: ${Spacing[4]};
`;

const PlanTitle = styled('h1')`
  ${Typography.HeadingMedium};
  margin: ${Spacing[0]};
  margin-bottom: -${Spacing[2]};
`;

const PlanSubtitleWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  column-gap: ${Spacing[1]};
  align-items: center;
`;

const PlanSubtitle = styled('div')`
  ${Typography.TextMedium};
  color: ${Color.CorePurple800};
`;

const TooltipContentWrapper = styled('div')`
  width: 300px;
`;

const PlanHeading = styled('h2')`
  ${Typography.TextLargeBold};
  padding-bottom: ${Spacing[1]};
`;

const UnorderedList = styled('ul')`
  margin-left: ${Spacing[4]};
  margin-bottom: ${Spacing[2]};
`;

const ListItem = styled('li')`
  padding-left: ${Spacing[1]};
  line-height: ${({ theme }) => theme.text.paragraph.md.lineHeight};
  margin-bottom: ${Spacing[4]};
  color: ${Color.CoreSlate800};
`;

const StartupProgramCard = styled(Card)`
  background-color: ${Color.PrimaryBlack};
`;

const StartupProgramContentWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  column-gap: ${Spacing[4]};
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;

  > svg {
    max-width: 300px;
  }
`;

const StartupProgramPlanTitle = styled(PlanTitle)`
  color: ${Color.PrimaryWhite};
`;

const StartupProgramParagraph = styled(Paragraph)`
  color: ${Color.PrimaryWhite};
  max-width: 650px;
`;

const StartupProgramButtons = styled('div')`
  display: flex;
  flex-direction: row;
  column-gap: ${Spacing[4]};
  flex-wrap: wrap;
`;

export default PlansTable;
